'use client'

import Link from 'next/link'
import { useMemo } from 'react'
import { reportClientError } from '@/lib/errors/report'

type BoundaryError = Error & { digest?: string }

type ErrorFallbackProps = {
  error: BoundaryError
  unstable_retry?: () => void
  reset?: () => void
  area?: string
  route?: string
  dashboardHref?: string
  publicPage?: boolean
}

export function ErrorFallback({
  error,
  unstable_retry,
  reset,
  area,
  route,
  dashboardHref = '/dashboard',
  publicPage = false,
}: ErrorFallbackProps) {
  const eventId = useMemo(() => {
    const id = reportClientError(error, {
      area: area ?? 'ui',
      action: 'route_error_boundary',
      route,
      severity: 'error',
      extra: {
        digest: error.digest,
      },
    })
    return id ?? error.digest ?? null
  }, [area, error, route])

  const description = useMemo(() => {
    if (publicPage) {
      return 'We could not load this page. Please try again.'
    }
    return 'We could not load this part of BuilderBuddi. Your work is safe. Try again, or return to the dashboard.'
  }, [publicPage])

  const retry = unstable_retry ?? reset

  return (
    <div className="flex min-h-[56vh] items-center justify-center px-4 py-8 sm:px-6">
      <div className="w-full max-w-xl rounded-3xl border border-[var(--bb-border)] bg-[var(--bb-surface)] p-6 shadow-[var(--bb-shadow-panel)] sm:p-8">
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--bb-foreground)]">Something went wrong.</h1>
        <p className="mt-3 text-sm leading-6 text-[var(--bb-text-secondary)]">{description}</p>

        {eventId && (
          <p className="mt-4 rounded-xl border border-[var(--bb-border)] bg-[var(--bb-surface-subtle)] px-3 py-2 text-xs text-[var(--bb-text-secondary)]">
            Reference: <span className="font-mono">{eventId}</span>
          </p>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {retry && (
            <button
              type="button"
              onClick={retry}
              className="inline-flex items-center justify-center rounded-full bg-[var(--bb-app-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--bb-app-primary-fg)] transition hover:bg-[var(--bb-app-primary-hover)]"
            >
              Try again
            </button>
          )}

          {!publicPage && (
            <Link
              href={dashboardHref}
              className="inline-flex items-center justify-center rounded-full border border-[var(--bb-border-strong)] px-5 py-2.5 text-sm font-semibold text-[var(--bb-foreground)] transition hover:bg-[var(--bb-surface-subtle)]"
            >
              Back to dashboard
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
